import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { TrendingUp, ArrowRight, Flag } from "lucide-react";
import { apiClient } from "@/lib/api";
import { BeltStripe, BELTS } from "@/components/BeltSystem";

/**
 * SkillJourney — visualizes the user's prompt-skill progression as a path
 * along the 8 AI Mastery belts.
 *
 * Design:
 * - 8 belt waypoints (White → Black), equally spaced along a connecting
 *   line. Each waypoint is a colored belt chip; the user's current belt is
 *   enlarged with its name shown.
 * - Day-1 belt carries a small flag tag above it (anchor).
 * - A "growth scale" bar shows micro-progress between the current belt's
 *   score floor and the next belt's score floor (e.g. 83 sits 33% of the
 *   way from Green Belt 81 → Blue Belt 87).
 * - History of checkpoints listed under the journey.
 */

// Score thresholds mirror BELT_TIERS[*][4] in backend/skill_check.py.
const BELT_WAYPOINTS = [
  { id: "white",  score: 0,  color: "#94A3B8", label: "White",  title: "Beginner" },
  { id: "yellow", score: 61, color: "#FACC15", label: "Yellow", title: "Operator" },
  { id: "orange", score: 73, color: "#F97316", label: "Orange", title: "Builder" },
  { id: "green",  score: 81, color: "#16A34A", label: "Green",  title: "Strategist" },
  { id: "blue",   score: 87, color: "#2563EB", label: "Blue",   title: "Creator" },
  { id: "purple", score: 92, color: "#7C3AED", label: "Purple", title: "Architect" },
  { id: "brown",  score: 96, color: "#92400E", label: "Brown",  title: "Master" },
  { id: "black",  score: 99, color: "#0F1419", label: "Black",  title: "AI Master" },
];

const SOURCE_LABEL = {
  initial: "Day 1 Skill Check",
  rewrite: "Rewrite",
  retake: "Retake",
  lesson_complete: "Lesson completed",
};

const LAST_BELT_CELEBRATED_KEY = "skill_journey_last_celebrated_belt";

/* ───────────── Belt-promotion celebration ─────────────
 *  Compares the user's freshly-loaded current belt against the last belt we
 *  celebrated for them (stored in localStorage). If the new belt outranks
 *  it, fires confetti + a sonner toast — once per promotion. Works the
 *  same whether the promotion came from a Skill Check rewrite, a retake,
 *  or simply completing enough lessons to cross a threshold. */
function celebrateBeltPromotionIfNeeded(currentBeltId) {
  if (!currentBeltId) return;
  let lastCelebrated;
  try { lastCelebrated = localStorage.getItem(LAST_BELT_CELEBRATED_KEY); } catch { lastCelebrated = null; }
  const newIdx = BELT_WAYPOINTS.findIndex((b) => b.id === currentBeltId);
  if (newIdx <= 0) {
    // White Belt is the starting point — nothing to celebrate yet, but
    // record it so the first real promotion fires.
    if (!lastCelebrated) {
      try { localStorage.setItem(LAST_BELT_CELEBRATED_KEY, currentBeltId); } catch (err) { console.warn("persist celebrated belt failed:", err); }
    }
    return;
  }
  const oldIdx = lastCelebrated ? BELT_WAYPOINTS.findIndex((b) => b.id === lastCelebrated) : -1;
  if (newIdx <= oldIdx) return; // already celebrated this (or higher) belt
  // Promotion! Fire confetti + toast.
  const newBelt = BELT_WAYPOINTS[newIdx];
  try {
    // Two confetti bursts from the lower-left and lower-right so it feels
    // celebratory across the screen, not just centered.
    confetti({
      particleCount: 90,
      angle: 60,
      spread: 60,
      origin: { x: 0.05, y: 0.9 },
      colors: [newBelt.color, "#FFFFFF", "#FACC15"],
    });
    confetti({
      particleCount: 90,
      angle: 120,
      spread: 60,
      origin: { x: 0.95, y: 0.9 },
      colors: [newBelt.color, "#FFFFFF", "#FACC15"],
    });
  } catch { /* confetti is best-effort; never crash the dashboard for a missing canvas */ }
  toast.success(`${newBelt.label} Belt unlocked`, {
    description: `You've leveled up to ${newBelt.title}. Keep training to advance.`,
    duration: 5500,
  });
  try { localStorage.setItem(LAST_BELT_CELEBRATED_KEY, currentBeltId); } catch (err) { console.warn("persist celebrated belt failed:", err); }
}

function formatDateShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function beltForScore(score) {
  let best = BELT_WAYPOINTS[0];
  let bestIdx = 0;
  BELT_WAYPOINTS.forEach((b, i) => {
    if (score >= b.score) { best = b; bestIdx = i; }
  });
  return { belt: best, index: bestIdx };
}

function growthToNext(score) {
  const { belt, index } = beltForScore(score);
  const next = BELT_WAYPOINTS[index + 1] || null;
  if (!next) {
    return { current: belt, next: null, pct: 100, pointsToGo: 0 };
  }
  const span = Math.max(1, next.score - belt.score);
  const into = Math.max(0, Math.min(span, score - belt.score));
  return {
    current: belt,
    next,
    pct: Math.round((into / span) * 100),
    pointsToGo: Math.max(0, next.score - score),
  };
}

export default function SkillJourney({ data: dataProp, compact = false, showRetake = true, refreshKey = 0 }) {
  const [data, setData] = useState(dataProp || null);
  const [loading, setLoading] = useState(!dataProp);
  // Guard so the celebration only fires once per mount/refresh, even if the
  // component re-renders before user state settles.
  const celebrationFiredRef = useRef(false);

  useEffect(() => {
    if (dataProp) {
      setData(dataProp);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiClient.get("/skill-journey")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ has_journey: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dataProp, refreshKey]);

  // After data lands, check if the user has crossed a belt threshold since
  // we last celebrated for them. Fires confetti + a sonner toast — exactly
  // once per (mount × refreshKey).
  useEffect(() => {
    if (!data || !data.has_journey) return;
    if (celebrationFiredRef.current) return;
    const currentBelt = beltForScore(Number(data.current_score ?? data.start_score ?? 0)).belt;
    celebrateBeltPromotionIfNeeded(currentBelt.id);
    celebrationFiredRef.current = true;
  }, [data]);

  // Reset the celebration guard whenever the parent triggers a fresh fetch
  // (e.g. after a rewrite re-grade). Otherwise a single mount could swallow
  // the second promotion of a session.
  useEffect(() => {
    celebrationFiredRef.current = false;
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="brut p-5 sm:p-6 animate-pulse" data-testid="skill-journey-loading">
        <div className="h-4 w-32 bg-[var(--surface)] rounded mb-3" />
        <div className="h-3 w-full bg-[var(--surface)] rounded" />
      </div>
    );
  }

  if (!data || !data.has_journey) {
    return <EmptyJourney compact={compact} />;
  }

  const start = Number(data.start_score ?? 0);
  const current = Number(data.current_score ?? start);
  const delta = Number(data.delta ?? Math.max(0, current - start));
  const startBelt = beltForScore(start);
  const currentBelt = beltForScore(current);
  const growth = growthToNext(current);

  return (
    <div
      className="brut p-5 sm:p-6"
      data-testid="skill-journey"
      style={{
        background: "linear-gradient(135deg, rgba(37,99,235,0.04) 0%, #FFFFFF 65%)",
        borderColor: "rgba(37,99,235,0.25)",
      }}
    >
      {/* ───── Heading ───── */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--ink-3)]">
            Your skill journey
          </div>
          <h3 className="font-display text-xl sm:text-2xl tracking-tight leading-tight mt-1">
            {delta > 0
              ? `+${delta} points since Day 1`
              : start === current
              ? "You're on the path."
              : `${delta} points since Day 1`}
          </h3>
        </div>
        <div
          className="px-3 py-1.5 rounded-lg font-bold text-xs tracking-wider flex-shrink-0"
          style={{
            background: currentBelt.belt.color,
            color: ["white", "yellow"].includes(currentBelt.belt.id) ? "#0F1419" : "white",
          }}
          data-testid="skill-journey-current-belt"
        >
          {currentBelt.belt.label} Belt
        </div>
      </div>

      {/* ───── Belt waypoints along the journey line ───── */}
      <BeltWaypoints currentIndex={currentBelt.index} startIndex={startBelt.index} />

      {/* ───── Growth scale toward next belt ───── */}
      <GrowthScale growth={growth} />

      {/* ───── CTA + history ───── */}
      {!compact && showRetake && (
        <div className="mt-5 pt-4 border-t border-[var(--line)]">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/skill-check"
              className="btn-duo inline-flex items-center justify-center gap-2 text-sm px-5 py-2.5"
              data-testid="skill-journey-retake"
            >
              Retake the Skill Check
              <ArrowRight size={14} />
            </Link>
            {data.event_count > 1 && (
              <span className="text-xs text-[var(--ink-3)]" data-testid="skill-journey-event-count">
                {data.event_count} skill checkpoints logged
              </span>
            )}
          </div>
        </div>
      )}

      {/* ───── Recent history ───── */}
      {!compact && data.history?.length > 1 && (
        <div className="mt-4 pt-4 border-t border-[var(--line)]" data-testid="skill-journey-history">
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--ink-3)] mb-2">
            Checkpoints
          </div>
          <ul className="space-y-1.5">
            {[...data.history].reverse().slice(0, 5).map((ev, i) => {
              const evBelt = beltForScore(ev.score).belt;
              return (
                <li
                  key={`evt-${i}`}
                  className="flex items-center justify-between gap-3 text-sm"
                  data-testid={`skill-journey-event-${i}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: evBelt.color }}
                    />
                    <span className="font-semibold text-[var(--ink)]">{evBelt.label} Belt</span>
                    <span className="text-[var(--ink-3)] truncate">· {SOURCE_LABEL[ev.source] || ev.source}</span>
                  </span>
                  <span className="text-xs text-[var(--ink-3)] flex-shrink-0">{formatDateShort(ev.taken_at)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ───────────── Belt waypoint rail ─────────────
 *  8 belt nodes equally spaced and connected by a line. The line up to the
 *  current belt is filled with the current belt color; beyond the current
 *  belt it is neutral. Day-1 belt carries a flag tag above it. Current belt
 *  shows its full name beneath. */
function BeltWaypoints({ currentIndex, startIndex }) {
  const n = BELT_WAYPOINTS.length;
  return (
    <div className="relative pt-8 pb-8" data-testid="skill-journey-waypoints">
      {/* Base connector line */}
      <div className="absolute left-0 right-0 top-[3.25rem] h-[3px] rounded-full bg-[var(--surface)]" />
      {/* Filled portion up to current */}
      <motion.div
        initial={{ width: `${(startIndex / (n - 1)) * 100}%` }}
        animate={{ width: `${(currentIndex / (n - 1)) * 100}%` }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-0 top-[3.25rem] h-[3px] rounded-full"
        style={{
          background: `linear-gradient(90deg, ${BELT_WAYPOINTS[startIndex].color} 0%, ${BELT_WAYPOINTS[currentIndex].color} 100%)`,
        }}
      />
      {/* Waypoint nodes */}
      <div className="relative grid" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}>
        {BELT_WAYPOINTS.map((b, i) => {
          const isCurrent = i === currentIndex;
          const isStart = i === startIndex;
          const reached = i <= currentIndex;
          // Look up the matching design-system belt so we can render a real
          // BeltStripe (with the contrasting stripe band) for the current
          // position. Falls back to a plain dot for the other waypoints.
          const dsBelt = BELTS.find((db) => db.id === b.id);
          return (
            <div
              key={b.id}
              className="relative flex flex-col items-center"
              data-testid={`skill-journey-waypoint-${b.id}`}
            >
              {/* Day-1 flag */}
              {isStart && (
                <div
                  className="absolute -top-1 flex flex-col items-center"
                  data-testid="skill-journey-day1-flag"
                >
                  <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-[var(--ink-3)] whitespace-nowrap leading-none">
                    Day 1
                  </div>
                  <Flag size={10} className="mt-0.5 text-[var(--ink-3)]" strokeWidth={2.5} />
                </div>
              )}
              {/* Waypoint marker — sits on the connector line */}
              <div className="relative mt-7 flex items-center justify-center" style={{ minHeight: 22 }}>
                {isCurrent && dsBelt ? (
                  <motion.div
                    initial={{ scale: 0.7, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="relative"
                    data-testid="skill-journey-current-marker"
                    style={{
                      filter: `drop-shadow(0 2px 6px ${dsBelt.color}66)`,
                    }}
                  >
                    {/* The real dojo belt (with stripe band) */}
                    <BeltStripe belt={dsBelt} size="md" />
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.45, delay: i * 0.04 }}
                    className="rounded-full"
                    style={{
                      width: 12,
                      height: 12,
                      background: reached ? b.color : "#FFFFFF",
                      border: reached ? "2px solid white" : `2px solid ${b.color}55`,
                      boxShadow: reached ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                    }}
                  />
                )}
              </div>
              {/* Label */}
              <div
                className={`mt-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.08em] text-center whitespace-nowrap leading-tight ${isCurrent ? "font-bold" : "font-semibold"}`}
                style={{
                  color: reached ? b.color : "var(--ink-4)",
                  opacity: isCurrent ? 1 : reached ? 0.85 : 0.5,
                }}
              >
                {b.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────── Growth scale ─────────────
 *  Micro-progress bar showing how far the user is into the current belt
 *  band toward the next belt. */
function GrowthScale({ growth }) {
  if (!growth.next) {
    return (
      <div className="mt-1 mb-1" data-testid="skill-journey-growth">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="font-semibold text-[var(--ink)]">Black Belt mastery reached</span>
          <span className="font-bold text-[var(--ink)] tabular-nums">100%</span>
        </div>
        <div className="h-2.5 rounded-full bg-[var(--surface)] overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: "100%",
              background: `linear-gradient(90deg, ${growth.current.color} 0%, ${growth.current.color} 100%)`,
            }}
          />
        </div>
      </div>
    );
  }
  return (
    <div className="mt-1 mb-1" data-testid="skill-journey-growth">
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-semibold text-[var(--ink)]">
          Progress to <span style={{ color: growth.next.color }}>{growth.next.label} Belt</span>
        </span>
        <span className="font-bold text-[var(--ink)] tabular-nums">{growth.pct}%</span>
      </div>
      <div className="h-2.5 rounded-full bg-[var(--surface)] overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${growth.pct}%` }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="h-full rounded-full"
          style={{
            background: `linear-gradient(90deg, ${growth.current.color} 0%, ${growth.next.color} 100%)`,
          }}
        />
      </div>
      <div className="text-xs text-[var(--ink-3)] mt-1.5">
        {growth.pointsToGo} {growth.pointsToGo === 1 ? "point" : "points"} to go · unlocks {growth.next.title}
      </div>
    </div>
  );
}

function EmptyJourney({ compact }) {
  return (
    <div className="brut p-5 sm:p-6" data-testid="skill-journey-empty">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[var(--primary,#2563EB)] text-white flex items-center justify-center flex-shrink-0">
          <TrendingUp size={18} />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--ink-3)]">
            Your skill journey
          </div>
          <h3 className="font-display text-xl tracking-tight leading-tight mt-1">
            Take your Day-1 Skill Check
          </h3>
          <p className="text-sm text-[var(--ink-2)] leading-relaxed mt-1.5">
            One short prompt task. We score your raw prompt skill so we can
            track your progress as you train.
          </p>
          {!compact && (
            <Link
              to="/skill-check"
              className="btn-duo mt-4 inline-flex items-center justify-center gap-2 text-sm px-5 py-2.5"
              data-testid="skill-journey-start-cta"
            >
              Start Skill Check <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
